import { inject, Injectable } from "@angular/core";

import type { BaseFilterDefinition, FilterValue } from "@filter/filter.types";
import type { IBaseCard, TranslateObject } from "@app/views-interfaces";
import { isJobsBillingCard } from "@app/views-interfaces";
import { IFirmGroupsService } from "@shared/ifirm-groups-service";
import { LanguageService } from "@services";
import { SimpleFilterMulti } from "./types/simple-filter-multi";

abstract class GroupFilterService extends SimpleFilterMulti<IBaseCard> {
    private readonly groupsService = inject(IFirmGroupsService);
    private readonly languageService = inject(LanguageService);
    protected abstract get groupNumber(): 1 | 2;
    protected abstract get source(): "Contact" | "Job";

    protected getDefinitionInfo(): BaseFilterDefinition {
        return {
            filterName: `${this.source}Group${this.groupNumber}`,
            label: `AllView.Group${this.groupNumber}`,
        };
    }

    protected getValue(item: IBaseCard): FilterValue | undefined {
        if (isJobsBillingCard(item)) {
            return this.source.toLowerCase() === "job"
                ? (item.job[`group${this.groupNumber}`] ?? undefined)
                : (item.contactInformation[`group${this.groupNumber}`] ?? undefined);
        }
        return item[`group${this.groupNumber}Id`] ?? undefined;
    }

    protected getLabel(value: string): string | undefined | null | TranslateObject {
        const options = this.groupNumber === 1 ? this.groupsService.getGroup1Options() : this.groupsService.getGroup2Options();
        return this.groupsService.getGroupOptionCaption(options, Number(value), this.languageService.language());
    }

    protected getAllValues(): readonly string[] {
        const options = this.groupNumber === 1 ? this.groupsService.getGroup1Options() : this.groupsService.getGroup2Options();
        return options?.map((option) => String(option.Id)) ?? [];
    }
}

@Injectable()
export class ContactGroup1FilterService extends GroupFilterService {
    protected get groupNumber(): 1 {
        return 1;
    }
    protected get source(): "Contact" {
        return "Contact";
    }
}

@Injectable()
export class ContactGroup2FilterService extends GroupFilterService {
    protected get groupNumber(): 2 {
        return 2;
    }
    protected get source(): "Contact" {
        return "Contact";
    }
}

@Injectable()
export class JobGroup1FilterService extends GroupFilterService {
    protected get groupNumber(): 1 {
        return 1;
    }
    protected get source(): "Job" {
        return "Job";
    }
}

@Injectable()
export class JobGroup2FilterService extends GroupFilterService {
    protected get groupNumber(): 2 {
        return 2;
    }
    protected get source(): "Job" {
        return "Job";
    }
}
